import React from 'react';
import { Leaf, Recycle, Sun, Droplet, Wind, TreePine } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Sustainability = () => {
  const initiatives = [
    {
      icon: Recycle,
      title: 'Geri Dönüştürülebilir Malzemeler',
      description: 'Tüm ürünlerimiz %100 geri dönüştürülebilir malzemelerden üretilmektedir. Üretim atıklarımızı da geri dönüşüme kazandırıyoruz.'
    },
    {
      icon: Sun,
      title: 'Yenilenebilir Enerji',
      description: 'Üretim tesislerimizde güneş enerjisi panelleri kullanarak enerji ihtiyacımızın önemli bir kısmını temiz enerji ile karşılıyoruz.'
    },
    {
      icon: Droplet,
      title: 'Su Tasarrufu',
      description: 'Üretim süreçlerimizde su tasarrufu sağlayan sistemler kullanıyor ve atık suları arıtarak yeniden kullanıyoruz.'
    },
    {
      icon: Wind,
      title: 'Karbon Ayak İzi Azaltma',
      description: 'Karbon emisyonumuzu azaltmak için enerji verimli makineler kullanıyor ve lojistik süreçlerimizi optimize ediyoruz.'
    },
    {
      icon: TreePine,
      title: 'Ağaçlandırma',
      description: 'Her yıl düzenli olarak ağaçlandırma kampanyaları düzenliyoruz ve doğaya katkı sağlıyoruz.'
    },
    {
      icon: Leaf,
      title: 'Yeyeşil Sertifikalar',
      description: 'ISO 14001 Çevre Yönetim Sistemi ve diğer yeyeşil sertifikalarla sürdürülebilirlik taaahhütlerimizi belgeleendiriyoruz.'
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="relative h-[400px] overflow-hidden mb-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1633533452148-a9657d2c9a5f?w=1920&q=80)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 to-green-700/70" />
        </div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="text-white">
            <h1 className="text-5xl lg:text-6xl font-bold mb-4">
              Sürdürülebilirlik
            </h1>
            <p className="text-xl max-w-2xl">
              Gelecek nesiller için daha yaşanabilir bir dünya bırakmak için çalışıyoruz.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Content */}
        <section className="mb-20">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Sürdürülebilirliği <span className="text-green-600">Ambalaj Sürecinde Seviyoruz!</span>
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  KargoPack olarak, sadece kaliteli ürünler üretmekle kalmayıp, aynı zamanda çevreye karşı sorumluluğumuzu da önemsiyor ve sürdürülebilir üretim süreçlerine büyük önem veriyoruz.
                </p>
                <p>
                  Tüm müşteri hizmetleri döngüsü boyunca sürdürülebilirliği hedefliyoruz. Çevre dostu üretim süreçlerimiz ve geri dönüştürülebilir malzemelerimiz ile geleceğe yatırım yapıyoruz.
                </p>
                <p>
                  ISO 14001 Çevre Yönetim Sistemi sertifikasına sahip olan şirketimiz, çevresel performansını sürekli iyileştirmeyi ve yasal gereklèliklere uyumu sağlamayı taaahhüt eder.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-blue-400/20 rounded-3xl blur-3xl" />
              <img
                src="https://images.unsplash.com/photo-1633533452148-a9657d2c9a5f?w=800&q=80"
                alt="Sürdürülebilirlik"
                className="relative rounded-3xl shadow-2xl w-full"
              />
            </div>
          </div>
        </section>

        {/* Initiatives Grid */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Sürdürülebilirlik Girişimlerimiz</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Çevre dostu üretim için attığımız adımlar ve devam eden projelerimiz.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {initiatives.map((initiative, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 border-0">
                <CardContent className="p-8">
                  <div className="w-16 h-16 mb-6 rounded-full bg-green-100 flex items-center justify-center">
                    <initiative.icon className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{initiative.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{initiative.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Stats Section */}
        <section className="bg-gradient-to-r from-green-600 to-green-700 rounded-3xl p-12 text-white">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">2024 Sürdürülebilirlik Hedeflerimiz</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">%50</div>
              <div className="text-lg">Yenilenebilir Enerji Kullanımı</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">%30</div>
              <div className="text-lg">Karbon Emisyonu Azaltma</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">%100</div>
              <div className="text-lg">Geri Dönüştürülebilir Ürün</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">10.000</div>
              <div className="text-lg">Dikilen Ağaç Sayısı</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Sustainability;
